import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

export type Id = number;

export interface AdminEndpointsResponse {
  applicationName?: string;
  version?: string;
  deployedVersion?: string;
  deployedApp?: string;
  endpoints: AdminEndpointInfo[];
}

export interface AdminEndpointInfo {
  type?: string;
  name?: string;
  url: string;
}

export interface AdminEntityTypeDto {
  key: string;
  name: string;
  fullName?: string;
  assemblyName?: string;
}

export interface AdminEntityFieldDto {
  name: string;
  type: string;
  isFilterable: boolean;
}

export interface FilterOperatorDto {
  displayName: string;
  expressionValue: string;
}

export interface AdminTestLoginRequest {
  applicationLoginId: Id;
}

/**
 * Postman-like runner payload:
 * POST /api/integration-tester/{integrationId}/send
 */
export interface SendEnvelopeDto {
  applicationLoginId: number;
  request: SendRequestDto;
}

export interface SendRequestDto {
  method: string;
  urlOrPath: string;
  target?: string | null;
  headers?: Record<string, string> | null;
  body?: string | null;
  contentType?: string | null;
  timeoutSeconds?: number | null;
}

/**
 * Response returned by your controller /send (it returns raw content).
 * Keep this if you still want typed responses.
 */
export interface SendResponseDto {
  statusCode: number;
  headers: Record<string, string[]>;
  body: string;
  durationMs: number;
}

export interface AdminLoginOptionDto {
  id: Id;
  description: string;
}

/**
 * Unified saved state (DB-backed): collections + requests
 */
export interface Kv {
  key: string;
  value: string;
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
export type BodyType = 'none' | 'json' | 'xml' | 'text';

export interface ItRequestDto {
  id: string; // PublicId
  name: string;
  description?: string | null;

  method: HttpMethod;
  url: string;

  params: Kv[];
  headers: Kv[];

  bodyType: BodyType;
  contentType?: string | null;
  body: string;

  sortOrder?: number;
}

export interface ItCollectionDto {
  id: string; // PublicId
  name: string;
  sortOrder?: number;
  requests: ItRequestDto[];
}

@Injectable({ providedIn: 'root' })
export class IntegrationTesterAdminService {
  private readonly baseUrl = '/api/integration-tester';

  constructor(private http: HttpClient) {}

  // -------------------------
  // Discovery / Metadata
  // -------------------------

  getAdminEndpoints(integrationId: Id): Observable<AdminEndpointsResponse> {
    return this.http.get<AdminEndpointsResponse>(`${this.baseUrl}/${integrationId}/admin-endpoints`);
  }

  getEntities(integrationId: Id): Observable<AdminEntityTypeDto[]> {
    return this.http.get<AdminEntityTypeDto[]>(`${this.baseUrl}/${integrationId}/entities`);
  }

  getEntityFields(integrationId: Id, entityKey: string): Observable<AdminEntityFieldDto[]> {
    const params = new HttpParams().set('entityKey', entityKey);
    return this.http.get<AdminEntityFieldDto[]>(`${this.baseUrl}/${integrationId}/entity-fields`, { params });
  }

  getFilterOperators(integrationId: Id): Observable<FilterOperatorDto[]> {
    return this.http.get<FilterOperatorDto[]>(`${this.baseUrl}/${integrationId}/filter-operators`);
  }

  getAdminLogins(integrationId: Id): Observable<AdminLoginOptionDto[]> {
    return this.http.get<AdminLoginOptionDto[]>(`${this.baseUrl}/${integrationId}/admin-logins`);
  }

  // -------------------------
  // Actions
  // -------------------------

  testLogin(integrationId: Id, applicationLoginId: Id): Observable<any> {
    const body: AdminTestLoginRequest = { applicationLoginId };
    return this.http.post<any>(`${this.baseUrl}/${integrationId}/test-login`, body);
  }

  send(integrationId: number, payload: SendEnvelopeDto): Observable<SendResponseDto> {
    return this.http.post<SendResponseDto>(`${this.baseUrl}/${integrationId}/send`, payload);
  }

  /**
   * Raw sender (useful because your controller returns ContentResult)
   */
sendRaw(integrationId: number, payload: SendEnvelopeDto): Observable<any> {
  return this.http.post(`/api/integration-tester/${integrationId}/send`, payload, {
    observe: 'response',
    responseType: 'text', // important: your controller returns Content(...) which can be json/xml/text
  }).pipe(
    map((resp) => {
      const headersDict: Record<string, string | string[]> = {};
      for (const k of resp.headers.keys()) {
        const all = resp.headers.getAll(k);
        headersDict[k] = (all && all.length > 1) ? all : (all?.[0] ?? resp.headers.get(k) ?? '');
      }

      const headersText = Object.keys(headersDict)
        .sort((a, b) => a.localeCompare(b))
        .map(k => {
          const v = headersDict[k];
          return `${k}: ${Array.isArray(v) ? v.join(', ') : v}`;
        })
        .join('\n');

      return {
        status: resp.status,
        headers: headersDict,
        headersText,
        body: resp.body ?? '',
      };
    })
  );
}

  // -------------------------
  // Collections (DB-backed)
  // -------------------------

  getCollections(integrationId: number): Observable<ItCollectionDto[]> {
    return this.http.get<ItCollectionDto[]>(`${this.baseUrl}/${integrationId}/collections`);
  }

  saveCollections(integrationId: number, collections: ItCollectionDto[]): Observable<{ ok: boolean }> {
    return this.http.put<{ ok: boolean }>(
      `${this.baseUrl}/${integrationId}/collections`,
      collections ?? []
    );
  }
}
